/*
  Keeps the Omega Facebook Chat widget above the mobile bottom toolbar
  (.hdt-toolbar) and the product-page sticky Add to Cart bar
  (.hdt-sticky-btn-atc) by publishing their combined visible clearance
  as the --hdt-chat-bottom-offset custom property on <html>.

  hdt-chat-clearance.css reads that variable to reposition the chat
  widget; this file never touches the widget element directly, so it
  keeps working even if Omega recreates its container/iframe.
*/
(() => {
  const TOOLBAR_SELECTOR = '.hdt-toolbar';
  const STICKY_ATC_SELECTOR = '.hdt-sticky-btn-atc';
  const BASE_SPACING = 16; // px gap kept above the topmost visible bar
  const CSS_VAR = '--hdt-chat-bottom-offset';

  const root = document.documentElement;
  let toolbarEl = document.querySelector(TOOLBAR_SELECTOR);
  let atcEl = document.querySelector(STICKY_ATC_SELECTOR);
  let rafId = null;

  const resizeObserver = new ResizeObserver(scheduleRecompute);
  if (toolbarEl) resizeObserver.observe(toolbarEl);
  if (atcEl) {
    resizeObserver.observe(atcEl);
    watchAtcTransition(atcEl);
  }

  function visibleTop(el) {
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    if (rect.top >= window.innerHeight || rect.bottom <= 0) return null;
    return rect.top;
  }

  function recompute() {
    rafId = null;
    const tops = [visibleTop(toolbarEl), visibleTop(atcEl)].filter((top) => top !== null);
    if (tops.length === 0) {
      root.style.removeProperty(CSS_VAR);
      return;
    }
    const clearance = Math.max(0, window.innerHeight - Math.min(...tops));
    root.style.setProperty(CSS_VAR, `${Math.round(clearance + BASE_SPACING)}px`);
  }

  function scheduleRecompute() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(recompute);
  }

  // The sticky ATC bar slides in/out over a CSS transition (see
  // assets/hdt-sticky-atc.css, `transition: transform .5s ...`), so a
  // recompute triggered the instant its `sticky-shown` class toggles
  // would sample its position mid-animation. Listening for the
  // transition to actually finish avoids capturing a stale, in-transit
  // position; the settle timeout is a fallback in case a transition is
  // interrupted or skipped (e.g. prefers-reduced-motion) and never
  // fires `transitionend`.
  const SETTLE_DELAY = 550; // ms, transition duration (500ms) + buffer

  function watchAtcTransition(el) {
    el.addEventListener('transitionend', scheduleRecompute, { passive: true });
  }

  function pickUpNewElements() {
    let foundNew = false;
    if (!toolbarEl) {
      toolbarEl = document.querySelector(TOOLBAR_SELECTOR);
      if (toolbarEl) {
        resizeObserver.observe(toolbarEl);
        foundNew = true;
      }
    }
    if (!atcEl) {
      atcEl = document.querySelector(STICKY_ATC_SELECTOR);
      if (atcEl) {
        resizeObserver.observe(atcEl);
        watchAtcTransition(atcEl);
        foundNew = true;
      }
    }
    if (foundNew) scheduleRecompute();
  }

  const bodyObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        scheduleRecompute();
        setTimeout(scheduleRecompute, SETTLE_DELAY);
      } else if (mutation.type === 'childList') {
        pickUpNewElements();
      }
    }
  });
  bodyObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
  });

  window.addEventListener('resize', scheduleRecompute, { passive: true });
  window.addEventListener('orientationchange', scheduleRecompute, { passive: true });

  recompute();
})();
